import { useMemo, useState } from 'react'

import { resourceListTag, useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  OWN_CATALOG_CAPABILITIES,
  OWN_GROUP_TAG,
  OWN_WINDOWS,
  OwnGroupsScreen,
  groupsVm,
  ownChangeGroup,
  ownCreateGroup,
  ownGroups,
  ownNomenclatureApi,
  type GroupDraft,
  type OwnGroupsActions,
} from '@krp/resource-own-ui'

import { useAppDispatch } from '../../app/hooks'
import { useOwnCapability } from './useOwnCapability'

const NEW_GROUP: GroupDraft = { id: null, name: '', parentId: '', archived: false }

/** Binding окна «Группы своей базы» (RP-OWN3): черновик формы — пока открыт экран; список — в кэше запроса. */
export function OwnGroupsBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const canWrite = useOwnCapability(resourceId, OWN_CATALOG_CAPABILITIES.catalogWrite)
  const args = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const groups = useQueryResult(ownGroups, args)

  const [draft, setDraft] = useState<GroupDraft | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [create, createState] = ownCreateGroup.useMutation()
  const [change, changeState] = ownChangeGroup.useMutation()
  const command = draft?.id == null ? createState : changeState

  const vm = groupsVm({ groups, draft, command, done, canWrite })

  const actions = useMemo<OwnGroupsActions>(
    () => ({
      create: () => {
        createState.reset()
        setDone(null)
        setDraft(NEW_GROUP)
      },
      edit: (groupId) => {
        const group = groups.data?.find((item) => item.id === groupId)
        if (group === undefined) return
        changeState.reset()
        setDone(null)
        setDraft({ id: group.id, name: group.name, parentId: group.parentId ?? '', archived: group.archived })
      },
      draftChanged: setDraft,
      closeForm: () => setDraft(null),
      save: () => {
        if (draft === null) return
        const name = draft.name.trim()
        const parentId = draft.parentId === '' ? null : draft.parentId
        const group = draft.id === null ? undefined : groups.data?.find((item) => item.id === draft.id)
        const request =
          draft.id === null
            ? create({ resourceId, name, parentId })
            : group === undefined
              ? null
              : change({ resourceId, groupId: draft.id, expectedVersion: group.version, name, parentId, archived: draft.archived })
        if (request === null) return
        void request
          .unwrap()
          .then((saved) => {
            setDone(draft.id === null ? `Группа «${saved.name}» создана` : `Группа «${saved.name}» сохранена`)
            setDraft(null)
          })
          .catch(() => undefined)
      },
      reload: () => dispatch(ownNomenclatureApi.util.invalidateTags([resourceListTag(OWN_GROUP_TAG, resourceId)])),
      openProducts: () => openWindow(OWN_WINDOWS.products, { rid: resourceId }),
    }),
    [change, changeState, create, createState, dispatch, draft, groups.data, openWindow, resourceId],
  )

  return <OwnGroupsScreen vm={vm} actions={actions} />
}
